import {useCallback} from 'react'
import {COLOR_NAME_TO_HEX, HEX_COLOR_PREFERRED_KEYS} from '../constants/hex-color-map'

const useHexColor = () => {
    const normalizeHexColorToken = useCallback((value) => {
        if (typeof value !== 'string') return null

        const color = value.trim()
        if (!color) return null

        if (/^#([\da-fA-F]{3}|[\da-fA-F]{6})$/.test(color)) return color
        if (/^([\da-fA-F]{3}|[\da-fA-F]{6})$/.test(color)) return `#${color}`

        const lowered = color.toLowerCase()
        return COLOR_NAME_TO_HEX[lowered] || null
    }, [])

    const normalizeHexColor = useCallback(
        (value) => {
            if (typeof value !== 'string') return null

            const normalizedTokens = value
                .split(',')
                .map((token) => normalizeHexColorToken(token))
                .filter(Boolean)

            if (!normalizedTokens.length) return null

            return normalizedTokens.join(',')
        },
        [normalizeHexColorToken],
    )

    const resolveHexColor = useCallback(
        (...candidates) => {
            for (const candidate of candidates) {
                const normalized = normalizeHexColor(candidate)
                if (normalized) return normalized
            }

            return null
        },
        [normalizeHexColor],
    )

    const getHexFromObject = useCallback((obj) => {
        if (!obj || typeof obj !== 'object') return null

        for (const key of HEX_COLOR_PREFERRED_KEYS) {
            if (obj?.[key]) return obj[key]
        }

        const dynamicHexKey = Object.keys(obj).find((key) => key.toLowerCase().includes('hex'))
        return dynamicHexKey ? obj[dynamicHexKey] : null
    }, [])

    return {
        normalizeHexColor,
        resolveHexColor,
        getHexFromObject,
    }
}

export default useHexColor
